/**
 * Optional tags chip input (spec §8.7).
 *
 * Type a tag and press Enter (or the 'Toevoegen' button) to create a chip.
 * Every chip has a labelled remove button (≥44px touch target). Adding is
 * case-insensitively de-duplicated; tags are trimmed and capped.
 */
import { useId, useState, type KeyboardEvent } from 'react'
import { Button, TextField } from '@/components/ui'
import { CloseIcon, PlusIcon } from '@/components/icons'

export const MAX_TAGS = 8
export const MAX_TAG_LENGTH = 24

export type TagsInputProps = {
  tags: string[]
  onChange: (tags: string[]) => void
  disabled?: boolean
}

/** Normalizes raw input into a tag; returns null when unusable. */
export function normalizeTag(raw: string): string | null {
  const tag = raw.trim().replace(/\s+/g, ' ').slice(0, MAX_TAG_LENGTH)
  return tag.length > 0 ? tag : null
}

/** Adds a tag (case-insensitive de-dupe, capped). Returns the new list. */
export function addTag(tags: string[], raw: string): string[] {
  const tag = normalizeTag(raw)
  if (!tag) return tags
  if (tags.some((existing) => existing.toLowerCase() === tag.toLowerCase())) return tags
  if (tags.length >= MAX_TAGS) return tags
  return [...tags, tag]
}

export function TagsInput({ tags, onChange, disabled = false }: TagsInputProps) {
  const inputId = useId()
  const [pending, setPending] = useState('')

  const commit = () => {
    const next = addTag(tags, pending)
    if (next !== tags) onChange(next)
    setPending('')
  }

  const removeTag = (tag: string) => {
    onChange(tags.filter((existing) => existing !== tag))
  }

  // TextField exposes no onKeyDown; Enter/comma commits via the wrapper.
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault()
      commit()
    }
  }

  return (
    <div className="journal-tags">
      <div className="journal-tags-input-row">
        <div className="journal-tags-field" onKeyDown={handleKeyDown}>
          <TextField
            id={inputId}
            label="Tags (optioneel)"
            value={pending}
            onChange={setPending}
            placeholder="Bijvoorbeeld: werk, gezin, sport"
            disabled={disabled}
            hint={`Maximaal ${MAX_TAGS} tags van ${MAX_TAG_LENGTH} tekens.`}
            onBlur={() => {
              if (pending.trim().length > 0) commit()
            }}
          />
        </div>
        <Button
          variant="secondary"
          onClick={commit}
          disabled={disabled || pending.trim().length === 0 || tags.length >= MAX_TAGS}
        >
          <PlusIcon size={18} />
          Toevoegen
        </Button>
      </div>

      {tags.length > 0 ? (
        <ul className="journal-tags-list" aria-label="Toegevoegde tags">
          {tags.map((tag) => (
            <li key={tag} className="journal-tag-chip">
              <span className="journal-tag-text">{tag}</span>
              <button
                type="button"
                className="journal-tag-remove"
                aria-label={`Verwijder tag ${tag}`}
                disabled={disabled}
                onClick={() => removeTag(tag)}
              >
                <CloseIcon size={16} />
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  )
}
